import { useState, useEffect } from 'react';
import { IconSparkle, PrepBoth, PrepMilk, PrepWater } from './BuilderUiIcons';
import { CategoryGlyph } from './CategoryGlyph';
import './RecommendedBlends.css';

function RecommendedBlends({ recommendations }) {
  const [showMobileNotification, setShowMobileNotification] = useState(false);

  useEffect(() => {
    // Show notification when recommendations appear
    if (recommendations && recommendations.length > 0) {
      setShowMobileNotification(true);
      // Auto-hide after 5 seconds
      const timer = setTimeout(() => {
        setShowMobileNotification(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [recommendations]);

  if (!recommendations || recommendations.length === 0) {
    return null;
  }

  const scrollToRecommendations = () => {
    const element = document.querySelector('.recommended-blends');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setShowMobileNotification(false);
    }
  };

  return (
    <>
      {showMobileNotification && (
        <div className="mobile-recommendations-notification" onClick={scrollToRecommendations}>
          <div className="notification-content">
            <span className="notification-icon" aria-hidden>
              <IconSparkle />
            </span>
            <div>
              <div className="notification-text">
                {recommendations.length} blend{recommendations.length > 1 ? 's' : ''} match!
              </div>
              <div className="notification-action">Tap to view</div>
            </div>
          </div>
          <button 
            className="notification-close"
            onClick={(e) => {
              e.stopPropagation();
              setShowMobileNotification(false);
            }}
          >
            ×
          </button>
        </div>
      )}
      
      <div className="recommended-blends">
      <h2 className="recommendations-title">
        <IconSparkle className="recommendations-title-icon" aria-hidden />
        You Might Also Like
      </h2>
      <p className="recommendations-subtitle">
        From what you&apos;ve composed, a few house blends we think you&apos;ll love.
      </p>
      
      <div className="recommendations-grid">
        {recommendations.map((recipe, index) => (
          <div key={recipe.recipe_id} className="recommendation-card">
            {(() => {
              // Handle comes from the API (src/utils/shopify_handle.py is the
              // single source of truth). No client-side slug guessing: if it's
              // missing, fail fast with the disabled link state below.
              const recipeHandle = recipe.shopify_handle || null;
              const recipeUrl = recipeHandle
                ? `https://${process.env.REACT_APP_SHOPIFY_DOMAIN || 'your-store.myshopify.com'}/products/${recipeHandle}`
                : null;
              return (
                <>
            <div className="recommendation-header">
              <div className="recommendation-rank">#{index + 1}</div>
            </div>
            
            <h3 className="recommendation-name">{recipe.recipe_name}</h3>
            <p className="recommendation-description">{recipe.description}</p>
            
            <div className="recommendation-details">
              <div className="detail-row">
                <span className="detail-icon" aria-hidden>
                  <IconSparkle size={16} />
                </span>
                <span className="detail-text">
                  <strong>Purposes:</strong> {recipe.purposes.join(', ')}
                </span>
              </div>
              
              <div className="detail-row">
                <span className="detail-icon" aria-hidden>
                  <CategoryGlyph categoryKey="herb" size={16} />
                </span>
                <span className="detail-text">
                  <strong>Ingredients:</strong> {recipe.common_ingredients} of {recipe.total_recipe_ingredients} match
                </span>
              </div>
              
              {recipe.preparation_method && (
                <div className="detail-row">
                  <span className="detail-icon" aria-hidden>
                    {recipe.preparation_method === 'water' ? (
                      <PrepWater size={16} />
                    ) : recipe.preparation_method === 'milk' ? (
                      <PrepMilk size={16} />
                    ) : (
                      <PrepBoth size={16} />
                    )}
                  </span>
                  <span className="detail-text">
                    <strong>Preparation:</strong> {recipe.preparation_method === 'water' ? 'Water-based' : 
                                                   recipe.preparation_method === 'milk' ? 'Milk-based' : 'Both'}
                  </span>
                </div>
              )}
            </div>
            
            {recipe.missing_from_blend && recipe.missing_from_blend.length > 0 && (
              <div className="missing-ingredients">
                <p className="missing-title">To complete this blend, add:</p>
                <div className="missing-list">
                  {recipe.missing_from_blend.slice(0, 3).map((ing, idx) => (
                    <span key={idx} className="missing-tag">
                      {ing.replace(/_/g, ' ')}
                    </span>
                  ))}
                  {recipe.missing_from_blend.length > 3 && (
                    <span className="missing-tag">
                      +{recipe.missing_from_blend.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {recipeUrl ? (
              <a
                href={recipeUrl}
                className="builder-action"
                target="_blank"
                rel="noopener noreferrer"
              >
                View this blend →
              </a>
            ) : (
              <span className="builder-action" aria-disabled="true" title="Product link unavailable">
                View this blend →
              </span>
            )}
                </>
              );
            })()}
          </div>
        ))}
      </div>
    </div>
    </>
  );
}

export default RecommendedBlends;
